(() => {
  const roots = document.querySelectorAll('[data-rebela-track]');
  if (!roots.length) return;

  const PK_TIME_ZONE = 'Asia/Karachi';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const text = (el, value) => {
    if (el) el.textContent = value ?? '—';
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: PK_TIME_ZONE,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(date);

    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    const day = get('day');
    const month = get('month');
    const year = get('year');
    const hour = get('hour');
    const minute = get('minute');
    const dayPeriod = get('dayPeriod').toUpperCase();

    return `${day} ${month} ${year} • ${hour}:${minute} ${dayPeriod} PKT`;
  };

  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  roots.forEach((root) => {
    root.classList.add('rebela-track--js');

    const endpoint = root.dataset.endpoint || '';
    const whatsapp = (root.dataset.whatsapp || '').replace(/\D/g, '');
    const reveals = [...root.querySelectorAll('[data-reveal]')];

    if ('IntersectionObserver' in window && !reducedMotion.matches) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      );
      reveals.forEach((el) => observer.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-visible'));
    }

    const form = root.querySelector('[data-track-form]');
    const statusEl = root.querySelector('[data-track-status]');
    const submit = root.querySelector('[data-track-submit]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const result = root.querySelector('[data-track-result]');
    const timeline = root.querySelector('[data-timeline]');
    const items = root.querySelector('[data-items]');
    const waWrap = root.querySelector('[data-whatsapp-wrap]');
    const waLink = root.querySelector('[data-whatsapp-link]');
    const tabs = [...root.querySelectorAll('[data-verify-tab]')];
    const panels = [...root.querySelectorAll('[data-verify-panel]')];
    const tabBar = root.querySelector('.rebela-track__verify-tabs');

    let verificationType = 'phone';

    const showStatus = (message, state = '') => {
      if (!statusEl) return;
      statusEl.textContent = message;
      if (state) statusEl.dataset.state = state;
      else statusEl.removeAttribute('data-state');
    };

    const setLoading = (loading) => {
      if (!submit) return;
      if (loading) {
        submit.setAttribute('aria-busy', 'true');
        submit.disabled = true;
        if (submitLabel) submitLabel.textContent = 'CHECKING ORDER';
      } else {
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
        if (submitLabel) submitLabel.textContent = 'TRACK MY ORDER';
      }
    };

    const setVerification = (type) => {
      verificationType = type === 'email' ? 'email' : 'phone';
      tabBar?.classList.toggle('is-email', verificationType === 'email');

      tabs.forEach((tab) => {
        const active = tab.dataset.verifyTab === verificationType;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const active = panel.dataset.verifyPanel === verificationType;
        panel.hidden = !active;
        const input = panel.querySelector('input');
        if (input) {
          input.required = active;
          if (!active) input.setCustomValidity('');
        }
      });

      showStatus('');
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setVerification(tab.dataset.verifyTab));
    });

    setVerification('phone');

    const renderTimeline = (entries) => {
      if (!timeline) return;
      timeline.replaceChildren();

      const list = Array.isArray(entries) ? entries : [];
      list.forEach((entry, index) => {
        const li = document.createElement('li');
        li.style.setProperty('--i', String(index));

        const isCurrent = entry.current === true || index === list.length - 1;
        li.classList.add(isCurrent ? 'is-current' : 'is-complete');

        const dot = document.createElement('span');
        dot.className = 'rebela-track__timeline-dot';
        dot.setAttribute('aria-hidden', 'true');

        const time = document.createElement('time');
        if (entry.at) {
          time.dateTime = entry.at;
          time.textContent = formatDate(entry.at);
        } else {
          time.textContent = 'Time not provided';
        }

        const copy = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = entry.label || entry.status || 'Update';
        copy.appendChild(strong);

        if (entry.message) {
          const paragraph = document.createElement('p');
          paragraph.textContent = entry.message;
          copy.appendChild(paragraph);
        }

        li.append(dot, time, copy);
        timeline.appendChild(li);
      });

      if (!list.length) {
        const li = document.createElement('li');
        li.classList.add('is-current');
        li.style.setProperty('--i', '0');

        const dot = document.createElement('span');
        dot.className = 'rebela-track__timeline-dot';
        dot.setAttribute('aria-hidden', 'true');

        const time = document.createElement('time');
        time.textContent = 'Latest status';

        const copy = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = 'PROCESSING';
        copy.appendChild(strong);

        li.append(dot, time, copy);
        timeline.appendChild(li);
      }
    };

    const renderItems = (list) => {
      if (!items) return;
      items.replaceChildren();

      (Array.isArray(list) ? list : []).forEach((item, index) => {
        const li = document.createElement('li');
        li.style.setProperty('--i', String(index));
        const parts = [
          item.title,
          item.variant,
          item.quantity ? `× ${item.quantity}` : '',
        ].filter(Boolean);
        li.textContent = parts.join(' · ');
        items.appendChild(li);
      });

      if (!items.children.length) {
        const li = document.createElement('li');
        li.style.setProperty('--i', '0');
        li.textContent = 'Order items available after verification.';
        items.appendChild(li);
      }
    };

    const replayResultMotion = () => {
      if (!result) return;
      result.classList.remove('is-ready');
      void result.offsetWidth;
      requestAnimationFrame(() => result.classList.add('is-ready'));
    };

    const render = (data) => {
      const order = data.order || {};

      text(root.querySelector('[data-order-status]'), order.status || 'Order status');
      text(root.querySelector('[data-order-tracking]'), order.trackingId || data.trackingId);
      text(root.querySelector('[data-order-id]'), order.orderId || order.name);
      text(root.querySelector('[data-customer-name]'), order.customerName);
      text(root.querySelector('[data-order-date]'), formatDate(order.orderDate));
      text(root.querySelector('[data-address]'), order.address || 'Address available after verification.');

      const lastUpdated = root.querySelector('[data-last-updated]');
      text(lastUpdated, order.lastUpdated ? `Last updated ${formatDate(order.lastUpdated)}` : '');

      const message = root.querySelector('[data-status-message]');
      if (message) {
        if (order.statusMessage) {
          message.textContent = order.statusMessage;
          message.hidden = false;
        } else {
          message.hidden = true;
        }
      }

      renderTimeline(order.timeline);
      renderItems(order.items);

      const tracking = order.trackingId || data.trackingId || '';
      if (waWrap && waLink) {
        if (whatsapp && tracking) {
          waLink.href = `https://wa.me/${whatsapp}?text=${encodeURIComponent(
            `Hi Rebela, I want WhatsApp tracking updates for Tracking ID ${tracking}.`
          )}`;
          waWrap.hidden = false;
        } else {
          waWrap.hidden = true;
        }
      }

      if (result) {
        result.hidden = false;
        replayResultMotion();
        requestAnimationFrame(() => {
          result.scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            block: 'start',
          });
        });
      }
    };

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();

      const formData = new FormData(form);
      const trackingId = String(formData.get('tracking_id') || '').trim().toUpperCase();

      if (trackingId.length < 8) {
        showStatus('Please enter a valid Rebela tracking ID.', 'error');
        return;
      }

      const payload = { trackingId, verificationType };

      if (verificationType === 'phone') {
        const phone = String(formData.get('phone') || '').trim();
        if (phone.replace(/\D/g, '').length < 10) {
          showStatus('Please enter the phone number used for your order.', 'error');
          return;
        }
        payload.phone = phone;
      } else {
        const email = String(formData.get('email') || '').trim();
        if (!validEmail(email)) {
          showStatus('Please enter the email address used for your order.', 'error');
          return;
        }
        payload.email = email;
      }

      if (!endpoint) {
        showStatus('Secure verification service is not connected yet.', 'error');
        return;
      }

      if (result) result.classList.remove('is-ready');
      setLoading(true);
      showStatus('Securely checking your tracking details…', 'loading');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        let data = {};
        try {
          data = await response.json();
        } catch {}

        if (!response.ok || data.verified !== true || !data.order) {
          if (result) result.hidden = true;
          throw new Error(
            data.message ||
              'We could not verify those details. Check your Tracking ID and verification information and try again.'
          );
        }

        render({ ...data, trackingId });
        showStatus('Order verified. Your latest journey is below.', 'success');
      } catch (error) {
        showStatus(error.message || 'Unable to check tracking right now. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    });
  });
})();
